/* global window */
import 'core-js/features/object';
import 'core-js/features/set';
import 'core-js/features/map';
import 'core-js/features/promise/all-settled';

import React from 'react';
import fetchPonyfill from 'fetch-ponyfill';
import ReactDOM from 'react-dom';
import { loadableReady } from '@loadable/component';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { IntlProvider, addLocaleData } from 'react-intl';
import { Provider } from 'react-redux';

import ConfigProvider from './components/ConfigProvider/ConfigProvider';
import CookieProvider from './components/CookieProvider/CookieProvider';
import LocalStorageProvider from './components/LocalStorageProvider/LocalStorageProvider';
import TrackerProvider from './components/TrackerProvider/TrackerProvider';
import store from './store';
import buildRoutes from './routes/Routes';
import { supportLocalStorage } from 'Helpers/localStorage';
import { checkLangPathParam } from 'Helpers/routeHelper';
import { HelmetProvider } from 'react-helmet-async';
import { initPlush, setTrackerProperties } from 'Helpers/pushNotification';
// import '@olx/laquesis-surveys/dist/surveys_panamera.css';
import uuid from 'uuid/v4';
import Auth from './Auth';
import { setBaseName } from 'Constants/basename';
import vwoHelper from 'Helpers/vwo-helper';
import VwoProvider from 'Components/VwoProvider/VwoProvider';

const { fetch } = fetchPonyfill();
const { config, props, vwoSettingsFile } = window.__APP;

setTrackerProperties({ props, config });

if (process.env.API_MODE === 'mock') {
    import(/* webpackChunkName: "cypress-bundle" */ '../../cypress/src/browser')
        .then(module => {
            module.worker.start({ quiet: true });
        });
}

function prepareRuntimeEnv() {
    try {
        // Remove object from gloabl scope.
        delete window.__APP;

        if (Auth.isLoggedIn()) {
            initPlush();
        }

        // console polyfill
        window.console = window.console || {
            log: () => { },
            error: () => { }
        };

        window.onpagehide = () => {
            if (supportLocalStorage() && window.sessionStorage.getItem('uuid') === window.localStorage.getItem('refreshingToken')) {
                window.localStorage.removeItem('refreshingToken');
            }
        };

        if (supportLocalStorage()) {
            window.sessionStorage.setItem('uuid', uuid());
        }

        return Promise.resolve();
    }
    catch (err) {
        return Promise.reject(err);
    }
}

const getPathParam = () => {
    const { pathname } = window.self.location;
    const firstPathParam = pathname.split('/')[1];

    if (checkLangPathParam(firstPathParam, config.langs)) {
        return firstPathParam;
    }

    return null;
};

function loadLocale() {
    let intlLoaded = Promise.resolve();
    let langParam = getPathParam() || config.lang;
    const params = langParam.split('-');

    langParam = params[0];
    const countryCode = params[1];
    const fullLangParam = params[1] ? `${langParam }-${ countryCode.toUpperCase()}` : langParam;

    if (!window.Intl) {
        intlLoaded = import(/* webpackChunkName: "intl" */'intl')
            .then(() => import(/* webpackChunkName: "locale-[request]" */`intl/locale-data/jsonp/${langParam}`));
    }

    const callback = data => {
        addLocaleData(require(`react-intl/locale-data/${langParam}`));
        return data[0];
    };

    return Promise.all([
        fetch(`/translations?lang=${fullLangParam}`).then(res => res.json()), intlLoaded
    ]).then(callback)
        .catch(() => callback([{}]));
}

async function vwoInstance(translations) {
    const initVWOSdk = vwoHelper.initVWOSdk;

    try {
        // const vwoSettingsFile = await Promise.all([
        //     vwoHelper.getSettingsFile(config?.vwo?.userId, config?.vwo?.sdkKey)
        // ]);

        return {
            translations,
            vwoClientInstance: initVWOSdk(vwoSettingsFile)
        };
    }
    catch {
        return {
            translations,
            vwoClientInstance: {}
        };
    }
}

const onError = error => {
    if (window && window.newrelic && window.newrelic.addPageAction) {
        const event = {};
        const errorParsed = error.includes('"') ? error.match(/"([^"]+)"/g).map(x => {
            return x.replace(/"/g, '');
        }) : null;

        if (errorParsed !== null && errorParsed.length === 2) {
            event.missingTranslation = errorParsed[0];
            event.language = errorParsed[1];
            window.newrelic.addPageAction('missingTranslation', event);
        }
    }
};

function renderApp(runTimeObject) {
    const routes = buildRoutes(config);
    const langParam = getPathParam();
    let getLocale = langParam || config.lang;

    getLocale = getLocale.split('-')[0];

    props.app_language = getLocale;
    const router = createBrowserRouter(routes, {
        basename: langParam ? `/${langParam}` : ''
    });

    setBaseName(router.basename);

    loadableReady(() => {
        ReactDOM.hydrate((
            <HelmetProvider>
                <IntlProvider locale={ getLocale } messages={ runTimeObject.translations } onError={ onError }>
                    <ConfigProvider source={ config }>
                        <CookieProvider>
                            <LocalStorageProvider>
                                <Provider store={ store }>
                                    <VwoProvider vwoClientInstance={ runTimeObject.vwoClientInstance }>
                                        <TrackerProvider properties={ props }>
                                            <RouterProvider router={ router } />
                                        </TrackerProvider>
                                    </VwoProvider>
                                </Provider>
                            </LocalStorageProvider>
                        </CookieProvider>
                    </ConfigProvider>
                </IntlProvider>
            </HelmetProvider>
        ), document.getElementById('app'));
    });
}
prepareRuntimeEnv()
    .then(loadLocale)
    .then(vwoInstance)
    .then(renderApp, err => {
        renderApp();
        throw err;
    });

